import React, { useState, useRef, useEffect } from "react";
import { Col, Row } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { addDirectDepositInfo } from "../../redux/thunks/userThunks";
import UserProfileInput from "./UserProfileInput";
import { Toast } from "primereact/toast";
import currencyData from "../../Currency/currency.json";
import countryData from "../../Country/country.json";
import "./SettingsAccountNewAccount.css";

const SettingsAccountNewAccount = () => {
  const [formData, setFormData] = useState({
    accountName: "",
    bankName: "",
    transitNumber: "",
    branchNumber: "",
    accountNumber: "",
    email: "",
    currency: null,
    country: null,
  });

  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const toast = useRef(null);

  const loginUser = useSelector((state) => state.user.user.data);

  useEffect(() => {
    if (loginUser?.email) {
      setFormData((prev) => ({ ...prev, email: loginUser.email }));
    }
  }, [loginUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCurrencyChange = (e) => {
    setFormData((prev) => ({ ...prev, currency: e.value }));
  };

  const handleCountryChange = (e) => {
    setFormData((prev) => ({ ...prev, country: e.value }));
  };

  const validate = () => {
    const temp = {};
    if (!formData.accountName) temp.accountName = "Required";
    if (!formData.bankName) temp.bankName = "Required";
    if (!formData.transitNumber) temp.transitNumber = "Required";
    if (!formData.branchNumber) temp.branchNumber = "Required";
    if (!formData.accountNumber) temp.accountNumber = "Required";
    if (!formData.currency) temp.currency = "Required";
    if (!formData.country) temp.country = "Required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      toast.current?.show({
        severity: "error",
        summary: "Stripe error",
        detail: "Stripe is not initialized",
      });
      return;
    }

    if (!validate()) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fix form errors",
      });
      return;
    }

    try {
      await dispatch(
        addDirectDepositInfo(
          formData,
          loginUser.account_enable,
          stripe,
          loginUser
        )
      );

      setFormData({
        accountName: "",
        bankName: "",
        transitNumber: "",
        branchNumber: "",
        accountNumber: "",
        email: loginUser.email || "",
        currency: "",
        country: "",
      });

      setErrors({});

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Deposit account added successfully!",
      });
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.message || "Failed to add account",
      });
    }
  };

  return (
    <div className="col-xl-10">
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Adding New Deposit Account</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="personal_validate">
            <div className="form-row">
              <UserProfileInput
                fieldLabel="Name on Account"
                name="accountName"
                value={formData.accountName}
                onChange={handleChange}
              />
              {errors.accountName && (
                <small className="text-danger">{errors.accountName}</small>
              )}

              <UserProfileInput
                fieldLabel="Name of Your Bank"
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
              />
              {errors.bankName && (
                <small className="text-danger">{errors.bankName}</small>
              )}

              <div className="form-group">
                <label htmlFor="currency">Account Currency</label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      currency: e.target.value,
                    }))
                  }
                  className="form-control"
                >
                  <option value="">Select currency</option>
                  {currencyData.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.code}
                    </option>
                  ))}
                </select>
                {errors.currency && (
                  <small className="text-danger">{errors.currency}</small>
                )}
              </div>
              {errors.currency && (
                <small className="text-danger">{errors.currency}</small>
              )}
              <div className="form-group">
                <label htmlFor="country">Country</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      country: e.target.value,
                    }))
                  }
                  className="form-control"
                >
                  <option value="">Select country</option>
                  {countryData.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {errors.country && (
                  <small className="text-danger">{errors.country}</small>
                )}
              </div>

              {errors.country && (
                <small className="text-danger">{errors.country}</small>
              )}

              <Row className="align-items-center">
                <Col xs={7}>
                  <UserProfileInput
                    fieldLabel="Branch Number"
                    name="branchNumber"
                    value={formData.branchNumber}
                    onChange={handleNumericChange}
                  />
                  {errors.branchNumber && (
                    <small className="text-danger">{errors.branchNumber}</small>
                  )}
                </Col>
              </Row>
              <Row className="align-items-center">
                <Col xs={7}>
                  <UserProfileInput
                    fieldLabel="Transit Number"
                    name="transitNumber"
                    value={formData.transitNumber}
                    onChange={handleNumericChange}
                  />
                  {errors.transitNumber && (
                    <small className="text-danger">
                      {errors.transitNumber}
                    </small>
                  )}
                </Col>
              </Row>

              <UserProfileInput
                fieldLabel="Account Number"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleNumericChange}
              />
              {errors.accountNumber && (
                <small className="text-danger">{errors.accountNumber}</small>
              )}

              <div className="form-group col-12 mt-3">
                <button type="submit" className="btn btn-success ps-5 pe-5">
                  Add Account
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <Toast ref={toast} />
    </div>
  );
};

export default SettingsAccountNewAccount;
